import { Box, Skeleton, Stack, useTheme } from "@mui/material";

const DISTRIBUTION_SKELETON_ROWS = ["first", "second", "third", "fourth", "fifth"] as const;

export const ProductScoreDistributionSkeleton = ({ label }: { label: string }) => {
    const theme = useTheme();

    return (
        <Stack
            aria-busy="true"
            aria-label={label}
            gap={theme.spacing(3)}
            role="status"
            data-testid="product-v2-score-distribution-skeleton"
        >
            <Stack
                aria-hidden="true"
                alignItems="center"
                data-testid="score-distribution-skeleton-modes"
                direction="row"
                flexWrap="wrap"
                gap={theme.spacing(2)}
            >
                <Skeleton height={theme.spacing(3)} variant="rounded" width={theme.spacing(12)} />
                <Stack direction="row" flexWrap="wrap" gap={theme.spacing(2)}>
                    <Skeleton height={theme.spacing(6)} variant="rounded" width={theme.spacing(18)} />
                    <Skeleton height={theme.spacing(6)} variant="rounded" width={theme.spacing(14)} />
                </Stack>
            </Stack>

            <Box
                aria-hidden="true"
                sx={{
                    alignItems: "flex-start",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: theme.spacing(3),
                }}
            >
                <Stack
                    data-testid="score-distribution-skeleton-summary"
                    gap={theme.spacing(1)}
                    sx={{
                        alignSelf: "start",
                        backgroundColor: theme.custom.grey[300],
                        borderRadius: theme.spacing(1.5),
                        boxSizing: "border-box",
                        maxWidth: "100%",
                        padding: theme.spacing(2),
                        width: { xs: "100%", lg: theme.custom.scoreDetail.summaryWidth },
                    }}
                >
                    <Stack alignItems="baseline" direction="row" gap={theme.spacing(1)}>
                        <Skeleton height={theme.spacing(7)} variant="rounded" width={theme.spacing(14)} />
                        <Skeleton height={theme.spacing(4)} variant="rounded" width={theme.spacing(8)} />
                    </Stack>
                    <Skeleton height={theme.spacing(4)} variant="rounded" width="100%" />
                </Stack>

                <Stack
                    data-testid="score-distribution-skeleton-breakdown"
                    gap={theme.spacing(2)}
                    sx={{
                        backgroundColor: theme.custom.grey[100],
                        borderRadius: theme.spacing(1.5),
                        boxShadow: theme.custom.shadows.grey,
                        boxSizing: "border-box",
                        flex: `1 1 ${theme.custom.scoreDetail.summaryWidth}`,
                        isolation: "isolate",
                        minWidth: 0,
                        padding: theme.spacing(2),
                        position: "relative",
                    }}
                >
                    {DISTRIBUTION_SKELETON_ROWS.map((row, index) => (
                        <Stack
                            direction={{ xs: "column", md: "row" }}
                            gap={theme.spacing(2)}
                            key={row}
                            sx={{
                                alignItems: { xs: "stretch", md: "center" },
                                borderBottomColor: theme.custom.strokes.containerDivider.color,
                                borderBottomStyle:
                                    index === DISTRIBUTION_SKELETON_ROWS.length - 1 ?
                                        "none"
                                    :   theme.custom.strokes.containerDivider.style,
                                borderBottomWidth:
                                    index === DISTRIBUTION_SKELETON_ROWS.length - 1 ?
                                        0
                                    :   theme.custom.strokes.containerDivider.width,
                                paddingBottom: index === DISTRIBUTION_SKELETON_ROWS.length - 1 ? 0 : theme.spacing(2),
                            }}
                        >
                            <Stack
                                alignItems="center"
                                direction="row"
                                gap={theme.spacing(1.5)}
                                minWidth={0}
                                sx={{
                                    flexBasis: { xs: "auto", md: theme.custom.scoreDetail.summaryWidth },
                                    flexGrow: { xs: 0, md: 1 },
                                    width: { xs: "100%", md: "auto" },
                                }}
                            >
                                <Skeleton height={theme.spacing(3)} variant="rounded" width={theme.spacing(3)} />
                                <Skeleton height={theme.spacing(3)} variant="rounded" width="100%" />
                            </Stack>
                            <Stack
                                alignItems="center"
                                direction="row"
                                gap={theme.spacing(2)}
                                sx={{
                                    maxWidth: "100%",
                                    width: "100%",
                                    [theme.breakpoints.up("md")]: {
                                        width: theme.custom.scoreDetail.lifecycleBreakdownWidth,
                                    },
                                }}
                            >
                                <Skeleton
                                    height={theme.spacing(2)}
                                    sx={{
                                        flexGrow: 1,
                                        [theme.breakpoints.up("md")]: {
                                            flexGrow: 0,
                                        },
                                    }}
                                    variant="rounded"
                                    width={theme.custom.scoreDetail.barWidth}
                                />
                                <Skeleton height={theme.spacing(3)} variant="rounded" width={theme.spacing(8)} />
                            </Stack>
                        </Stack>
                    ))}
                    <Box
                        sx={{
                            borderColor: theme.custom.strokes.containerDivider.color,
                            borderRadius: theme.spacing(1.5),
                            borderStyle: theme.custom.strokes.containerDivider.style,
                            borderWidth: theme.custom.strokes.containerDivider.width,
                            boxSizing: "border-box",
                            inset: 0,
                            pointerEvents: "none",
                            position: "absolute",
                        }}
                    />
                </Stack>
            </Box>
        </Stack>
    );
};
